'use client';

import React, { useRef, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faCaretDown } from '@fortawesome/pro-solid-svg-icons';
import { useClickOutside } from 'src/hooks/common/useClickOutside';
import { PulseRadio } from 'src/components/PulseUI';

interface RadioButtonSelectProps {
  id: string;
  header: string;
  selectOnChange: (value: string) => void;
  selectedOption: string;
}

const RadioButtonSelect = ({
  id,
  header,
  selectOnChange,
  selectedOption,
}: RadioButtonSelectProps) => {
  const timeframeOptions = [
    { value: 'all', label: 'All' },
    { value: 'current_week', label: 'Current Week' },
    { value: 'current_month', label: 'Current Month' },
    { value: 'current_quarter', label: 'Current Quarter' },
    { value: 'last_six_months', label: 'Past 6 Months' },
  ];

  const displayValue = timeframeOptions.find(
    (option) => option.value === selectedOption
  )?.label;

  const triggerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });

  const updateMenuPosition = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
      });
    }
  };

  const handleOpenMenu = () => {
    if (!isOpen) {
      updateMenuPosition();
    }
    setIsOpen(!isOpen);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target?.value;
    selectOnChange(value);
    handleClose();
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  useClickOutside([menuRef, triggerRef], handleClose, isOpen);

  // Update position on scroll/resize when menu is open
  useEffect(() => {
    if (isOpen) {
      window.addEventListener('scroll', updateMenuPosition, true);
      window.addEventListener('resize', updateMenuPosition);
      return () => {
        window.removeEventListener('scroll', updateMenuPosition, true);
        window.removeEventListener('resize', updateMenuPosition);
      };
    }
  }, [isOpen]);

  return (
    <>
      <div ref={triggerRef} className="w-full">
        <button
          id={id}
          onClick={handleOpenMenu}
          className="inline-flex items-center justify-between w-full min-h-8 px-3 py-1.5 rounded-full border border-neutral-200 bg-white text-sm font-semibold"
        >
          <span className="flex-1 text-left text-neutral-800 truncate font-normal">
            {displayValue}
          </span>
          <Icon
            icon={faCaretDown}
            className={`w-3.5 h-3.5 text-neutral-500 transition-transform duration-200 ${
              isOpen ? '-rotate-90' : 'rotate-0'
            }`}
          />
        </button>
      </div>

      {isOpen &&
        createPortal(
          <div
            ref={menuRef}
            className="fixed z-50 w-60 p-2 flex flex-col items-start rounded-lg border border-neutral-300 bg-white shadow-md"
            style={{
              top: menuPosition.top,
              left: menuPosition.left,
            }}
          >
            <div className="w-full">
              <h3 className="text-sm font-medium ml-2 mt-2">{header}</h3>

              <div className="pt-2 max-h-52 overflow-y-auto">
                {timeframeOptions?.map((option) => {
                  return (
                    <div
                      key={option.value}
                      className="flex items-center w-full mx-2 my-1.5 gap-3 h-6"
                    >
                      <PulseRadio
                        label={option.label}
                        value={option.value}
                        onChange={handleChange}
                        checked={selectedOption === option.value}
                        size="sm"
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default RadioButtonSelect;
